import { PortalContainerProvider } from '@crono-one/design-system';
import FormInput from 'components/CreationForms/base/FormInput';
import FormSection from 'components/CreationForms/base/FormSection';
import AiFieldsEnrichPicker from 'components/CreationForms/fields/AiFieldsEnrichPicker';
import CompanyNameField from 'components/CreationForms/fields/CompanyNameField';
import ContactMethodField from 'components/CreationForms/fields/ContactMethodField';
import IndustryField from 'components/CreationForms/fields/IndustryField';
import OwnerPicker from 'components/CreationForms/fields/OwnerPicker';
import SyncTypePicker from 'components/CreationForms/fields/SyncTypePicker';
import {
  ACCOUNT_CONTACT_METHOD_ROWS,
  ACCOUNT_INFO_FIELDS,
  CompanyForm,
  NAME_LABEL,
} from 'components/CreationForms/forms/CompanyForm/useCompanyForm';
import ListSection from 'components/CreationForms/sections/ListSection';
import OtherFieldsSection from 'components/CreationForms/sections/OtherFieldsSection';
import TagsSection from 'components/CreationForms/sections/TagsSection';
import { getContactMethodLink } from 'components/CreationForms/utils/fields';
import ExternalLinkButton from 'components/ExternalLinkButton';
import LinkedinCompanySearchDialog from 'components/LinkedinCompanySearchDialog';
import { LinkedinCompanyCompleteInfo } from 'crono-fe-common/types/crono-extension/linkedin';
import { ListType } from 'crono-fe-common/types/list';
import { useState } from 'react';

export interface CompanyFormBodyProps {
  form: CompanyForm;
}

/** The sections of a single company form, for the state of `useCompanyForm`. */
const CompanyFormBody = ({ form }: CompanyFormBodyProps) => {
  const {
    company,
    name,
    nameCheck,
    nativeFields,
    syncType,
    ownerId,
    listSelection,
    customFields,
    aiFields,
    credits,
  } = form;
  const [isLinkedinSearchOpen, setIsLinkedinSearchOpen] = useState(false);

  const handleLinkedinCompanyPick = (info: LinkedinCompanyCompleteInfo) => {
    form.handleLinkedinCompanyPick(info);
    setIsLinkedinSearchOpen(false);
  };

  return (
    <>
      {syncType.isChoiceVisible && (
        <FormSection>
          <SyncTypePicker
            createInCrm={syncType.createInCrm}
            onChange={syncType.setPickedCreateInCrm}
          />
        </FormSection>
      )}

      <FormSection title="Company owner">
        <OwnerPicker ownerId={ownerId} onChange={form.setOwnerId} />
      </FormSection>

      <FormSection title="Company info">
        <CompanyNameField
          label={NAME_LABEL}
          required
          isTaken={nameCheck.isTaken}
          value={company.name}
          onChange={(value) => form.setCompanyField('name', value)}
          onSearchLinkedin={() => setIsLinkedinSearchOpen(true)}
        />
        {ACCOUNT_INFO_FIELDS.map(({ field, label, placeholder, type }) =>
          field === 'industry' ? (
            <IndustryField
              key={field}
              label={label}
              placeholder={placeholder}
              required={nativeFields.isRequired(field)}
              value={company[field]}
              onChange={(value) => form.setCompanyField(field, value)}
            />
          ) : (
            <FormInput
              key={field}
              label={label}
              placeholder={placeholder}
              required={nativeFields.isRequired(field)}
              type={type}
              value={company[field]}
              onChange={(value) => form.setCompanyField(field, value)}
            />
          ),
        )}
      </FormSection>

      <FormSection title="Company details" className="gap-2">
        {ACCOUNT_CONTACT_METHOD_ROWS.map(({ method, label, placeholder }) => {
          const link = getContactMethodLink(method, 'company', company[method]);
          return (
            <ContactMethodField
              key={method}
              method={method}
              label={label}
              placeholder={placeholder}
              required={nativeFields.isRequired(method)}
              endAddon={link && <ExternalLinkButton {...link} />}
              value={company[method]}
              onChange={(value) => form.setCompanyField(method, value)}
            />
          );
        })}
      </FormSection>

      {aiFields.hasAiFields && (
        <FormSection title="Find company data" className="gap-2">
          <AiFieldsEnrichPicker
            tableType="Account"
            creditsLeft={credits?.aiLeft}
            selectedIds={aiFields.selectedIds}
            onChange={aiFields.setSelectedIds}
          />
        </FormSection>
      )}

      <TagsSection
        tags={customFields.tagProperties}
        values={customFields.values}
        onTagClick={customFields.toggleTagOption}
      />

      <ListSection
        listType={ListType.Account}
        selectedList={listSelection.selectedList}
        onChange={listSelection.setSelectedList}
      />

      <OtherFieldsSection
        properties={customFields.otherFieldProperties}
        values={customFields.values}
        onChange={customFields.setValue}
      />

      {isLinkedinSearchOpen && (
        // Portalled to the body: inside the drawer the drawer's transform
        // would pin the dialog's fixed overlay to the drawer.
        <PortalContainerProvider container={null}>
          <LinkedinCompanySearchDialog
            title="Please select one:"
            searchText={name}
            onPick={handleLinkedinCompanyPick}
            onClose={() => setIsLinkedinSearchOpen(false)}
          />
        </PortalContainerProvider>
      )}
    </>
  );
};

export default CompanyFormBody;
